const IDENTIFIER_REGEX = /^(?:(\d+)-)?([A-Z]{3})(\d{5}|995)([A-Z])$/;

const KVPS_BRAND_LOGO: Record<string, string> = {
  A: "/brands/audi.png",
  C: "/brands/skoda.png",
  N: "/brands/volkswagen_utility.png",
  S: "/brands/seat.png",
  V: "/brands/volkswagen.png",
};

/** Single-letter brand code embedded in KVPS identifiers (matches Prisma `BrandLetter`). */
export type KvpsBrandLetter = "A" | "V" | "N" | "S" | "C";

/**
 * Maps LEMA export column "Marque métier" (VW, VU, AUDI, …) to the KVPS brand letter.
 * Used by LEMA seed/import; keep in sync with business rules from the referential team.
 */
export function mapLemaBusinessBrandToKvpsBrandLetter(
  raw: string | null | undefined,
): KvpsBrandLetter | null {
  const normalized = raw?.trim().toUpperCase().replace(/\s+/g, " ") ?? "";
  if (!normalized) return null;

  switch (normalized) {
    case "VW":
      return "V";
    case "VU":
      return "N";
    case "AUDI":
      return "A";
    case "SKODA":
      return "C";
    case "SEAT":
    case "CUPRA":
      return "S";
    default:
      return null;
  }
}

export type Kvps = {
  identifier: string;
  label: string;
  parts: {
    prefix: string; // TODO: might be renamed later...
    country: string;
    wholesalerNumber: string;
    brand: string;
  };
  kind: KvpsKind;
};

export enum KvpsKind {
  AVS,
  INTERNAL,
  RETAIL,
  UNKNOWN,
}

export function buildKvps(
  identifier: Kvps["identifier"],
  label: Kvps["label"],
) {
  const match = identifier.match(IDENTIFIER_REGEX);

  if (!match) {
    throw new Error(`Invalid KVP identifier: ${identifier}`);
  }

  const kvps: Kvps = {
    identifier,
    label,
    parts: {
      prefix: match[1],
      country: match[2],
      wholesalerNumber: match[3],
      brand: match[4],
    },
    kind: KvpsKind.UNKNOWN,
  };

  kvps.kind = getKvpsKind(kvps);

  return kvps;
}

export function findKvps(
  identifier: Kvps["identifier"],
  candidates: Kvps[],
): Kvps | undefined {
  return candidates.find((kvp) => kvp.identifier === identifier);
}

export function formatKvps(kvps: Kvps) {
  return `${kvps.identifier} – ${kvps.label}`;
}

export function getKvpsBrandLogoPath(
  kvps: Kvps | null | undefined,
): string | null {
  if (!kvps?.parts?.brand) return null;
  return KVPS_BRAND_LOGO[kvps.parts.brand] ?? null;
}

export function getKvpsKind(kvps: Kvps): KvpsKind {
  if (kvps.parts.country !== "FRA") {
    return KvpsKind.UNKNOWN;
  }

  if (kvps.parts.wholesalerNumber === "995") {
    return KvpsKind.INTERNAL;
  }

  // TODO:
  if (false) {
    return KvpsKind.AVS;
  }

  return KvpsKind.RETAIL;
}
